import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { memo } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryImageItem } from '@/components/commons/gallery-image-item'
import { ButtonLegacy } from '@/components/ui/button-legacy'
import { Expand01Icon, MoveIcon, Trash01Icon } from '@/components/ui/icons.tsx'
import { Spinner } from '@/components/ui/spinner'
import type { ExperienceGalleryImage } from '@/features/experience-shell/types.ts'
import {
	OpsVettingDisabledContainer,
	OpsVettingDragButton,
	OpsVettingGalleryItemsContainer,
	OpsVettingImageTitle
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useTranslation } from '@/translations/i18n.tsx'
import { useDeleteExperienceVettingPhotos } from '../queries/use-delete-experience-vetting-photos.ts'

export const ExpVettingCriteriaGalleryUploadedItem = memo(
	({
		image,
		galleryId,
		experienceId,
		hideAction,
		onClickExpand
	}: {
		image: ExperienceGalleryImage
		galleryId: number
		experienceId: number
		hideAction: boolean
		onClickExpand?: () => void
	}) => {
		const { t } = useTranslation()

		const deletePhoto = useDeleteExperienceVettingPhotos({
			galleryId,
			experienceId
		})

		const onClickDeletePhoto = (e: React.MouseEvent<HTMLButtonElement>) => {
			e.stopPropagation()
			deletePhoto.mutate({ imageIds: [image.id] })
		}

		const {
			attributes,
			setNodeRef,
			transform,
			transition,
			isDragging,
			listeners
		} = useSortable({ id: image.id })

		const style = {
			transform: CSS.Translate.toString(transform),
			transition
		}

		return (
			<OpsVettingDisabledContainer
				$isDisabled={isDragging}
				ref={setNodeRef}
				style={style}
				{...attributes}
			>
				<OpsVettingGalleryItemsContainer direction={'column'} gap={4}>
					<GalleryImageItem
						bottomLeftSlot={
							<Chip
								label={t(
									'vetting:criteria.service.gallery.internal_upload'
								)}
								variant={'neutral'}
							/>
						}
						bottomRightSlot={
							!hideAction && (
								<ButtonLegacy
									className='show-on-hover'
									disabled={deletePhoto.isPending}
									onClick={onClickExpand}
									shape='square'
									size='sm'
									type='button'
									variant='secondary'
								>
									<Expand01Icon />
								</ButtonLegacy>
							)
						}
						src={image.thumbnails?.xs || image.url}
						topLeftSlot={
							hideAction ? null : (
								<OpsVettingDragButton
									disabled={deletePhoto.isPending}
									{...listeners}
									$isDragging={isDragging}
									className='show-on-hover'
									shape='square'
									size='sm'
									type='button'
									variant='secondary'
								>
									<MoveIcon size={16} />
								</OpsVettingDragButton>
							)
						}
						topRightSlot={
							<ButtonLegacy
								disabled={deletePhoto.isPending || hideAction}
								onClick={onClickDeletePhoto}
								shape='square'
								type='button'
								variant={'secondary'}
							>
								{deletePhoto.isPending ? (
									<Spinner />
								) : (
									<Trash01Icon />
								)}
							</ButtonLegacy>
						}
					/>
					<OpsVettingImageTitle>{image.name}</OpsVettingImageTitle>
				</OpsVettingGalleryItemsContainer>
			</OpsVettingDisabledContainer>
		)
	}
)
